import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import * as Babel from "@babel/standalone";

// Components available in live preview
import {
  Box as MBox,
  Typography as MTypography,
  CircularProgress as MCircularProgress,
  Grid as MGrid,
} from "@mui/material";

const scope = { React, MBox, MTypography, MCircularProgress, MGrid };

const PrintDocBuilder: React.FC = () => {
  // Header, Body, Footer fields
  const [header, setHeader] = useState("Quotation Header Line 1\nQuotation Header Line 2");
  const [body, setBody] = useState("Customer: John Doe\nSite: 123 Main Street\nQuotation No: 001\nDate: 30-09-2025");
  const [footer, setFooter] = useState("Thanks & Regards,\nJOSEPH PJ\nManaging Director");

  const [footerAlign, setFooterAlign] = useState<"left" | "center" | "right">("right");
  const [debouncedCode, setDebouncedCode] = useState("");

  // Build TSX code dynamically
  useEffect(() => {
    const code = `
function MyPreview() {
  return (
    <MBox p={2} border={1} borderRadius={2}>
      {/* Header */}
      <MBox mb={2}>
        {${JSON.stringify(header.split("\n"))}.map((line, idx) => (
          <MTypography key={idx} variant={idx === 0 ? "h4" : "h6"} gutterBottom>
            {line}
          </MTypography>
        ))}
      </MBox>

      {/* Body */}
      <MBox mb={2}>
        {${JSON.stringify(body.split("\n"))}.map((line, idx) => (
          <MTypography key={idx}>{line}</MTypography>
        ))}
      </MBox>

      {/* Footer */}
      <MBox mt={4} textAlign="${footerAlign}">
        {${JSON.stringify(footer.split("\n"))}.map((line, idx) => (
          <MTypography key={idx} fontWeight={idx === 1 ? "bold" : "normal"}>
            {line}
          </MTypography>
        ))}
      </MBox>
    </MBox>
  );
}
`;
    setDebouncedCode(code);
  }, [header, body, footer, footerAlign]);

  // Render preview safely
  const renderPreview = () => {
    try {
      const transformed = Babel.transform(debouncedCode, {
        presets: ["typescript", "react"],
        filename: "file.tsx",
      }).code;

      const Component = new Function(
        "React",
        ...Object.keys(scope),
        `${transformed}; return MyPreview;`
      )(React, ...Object.values(scope));

      return <Component />;
    } catch (err: any) {
      return (
        <MTypography color="error" sx={{ whiteSpace: "pre-wrap" }}>
          {err.message}
        </MTypography>
      );
    }
  };

  return (
    <Grid container spacing={2}>
      {/* Left: Input Fields */}
      <Grid item xs={6}>
        <Box p={2} border={1} borderRadius={2}>
          <Typography variant="h6" gutterBottom>
            Header
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            value={header}
            onChange={(e) => setHeader(e.target.value)}
            variant="outlined"
            sx={{ mb: 2 }}
          />

          <Typography variant="h6" gutterBottom>
            Body
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={8}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            variant="outlined"
            sx={{ mb: 2 }}
          />

          <Typography variant="h6" gutterBottom>
            Footer
          </Typography>
          <FormControl fullWidth sx={{ mb: 1 }}>
            <InputLabel>Footer Align</InputLabel>
            <Select
              value={footerAlign}
              label="Footer Align"
              onChange={(e) => setFooterAlign(e.target.value as any)}
            >
              <MenuItem value="left">Left</MenuItem>
              <MenuItem value="center">Center</MenuItem>
              <MenuItem value="right">Right</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            minRows={4}
            value={footer}
            onChange={(e) => setFooter(e.target.value)}
            variant="outlined"
          />
        </Box>
      </Grid>

      {/* Right: Live Preview */}
      <Grid item xs={6}>
        <Box p={2} border={1} borderRadius={2}>
          <Typography variant="h6" gutterBottom>
            Live Preview
          </Typography>
          <Box mt={2} p={2} sx={{ border: "1px dashed gray", minHeight: "500px" }}>
            {renderPreview()}
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default PrintDocBuilder;
